import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArchiveRestore, RotateCcw, Trash2, Loader2 } from "lucide-react";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));

export function RecycleBinButton({ onRestored }) {
  const { user } = useAuth();
  const owner = ["master_admin", "super_admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/recycle-bin").then((r) => setRows(r.data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  async function restore(ids, key) {
    setBusy(key);
    try {
      const { data } = await api.post("/recycle-bin/restore", { ids });
      toast.success(`${data.restored} restored${data.skipped ? ` · ${data.skipped} skipped (already in the list again)` : ""}`); load(); onRestored();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }
  async function purge(ids, key) {
    if (!window.confirm(`Permanently delete ${ids.length} student${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBusy(key);
    try { await api.post("/recycle-bin/purge", { ids }); toast.success("Deleted forever"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }
  const all = (rows || []).map((r) => r.id);
  return (
    <>
      <Button data-testid="recycle-bin-btn" variant="outline" onClick={() => { setOpen(true); setRows(null); load(); }} className="h-11 px-4">
        <ArchiveRestore size={16} className="mr-2" /> Recycle bin
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Recycle bin</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Deleted students stay here for 30 days with their attendance, daily activity, reports and fees, then are removed for good.</p>
          {rows === null ? <Loader2 className="animate-spin text-violet-500" /> : rows.length === 0 ? <p data-testid="recycle-bin-empty" className="text-sm text-slate-500 py-6 text-center">The recycle bin is empty.</p> : (<>
            <div className="flex gap-2">
              <Button size="sm" data-testid="recycle-restore-all" disabled={!!busy} onClick={() => restore(all, "all")} className="btn-brand">{busy === "all" ? <Loader2 size={14} className="animate-spin mr-1" /> : <RotateCcw size={14} className="mr-1" />}Restore all ({rows.length})</Button>
              {owner && <Button size="sm" variant="outline" data-testid="recycle-purge-all" disabled={!!busy} onClick={() => purge(all, "pall")} className="text-rose-600">Empty bin</Button>}
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.id} data-testid={`recycle-row-${r.id}`} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{r.name} <span className="text-slate-400 font-normal">{r.admission_number}</span></div>
                    <div className="text-xs text-slate-500">{r.class_name} · deleted {fmtDate(r.deleted_at.slice(0, 10))} by {r.deleted_by} · <span className="text-amber-700">{daysLeft(r.expires_at)} days left</span></div>
                  </div>
                  <Button size="sm" variant="outline" disabled={!!busy} data-testid={`recycle-restore-${r.id}`} onClick={() => restore([r.id], r.id)}>{busy === r.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}</Button>
                  {owner && <Button size="sm" variant="ghost" disabled={!!busy} data-testid={`recycle-purge-${r.id}`} onClick={() => purge([r.id], `p${r.id}`)} className="text-rose-600"><Trash2 size={14} /></Button>}
                </div>
              ))}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
}
